/**
 * Who shot down what, and what could be said about it afterwards.
 *
 * The running tallies here are the debrief's `VICTORIES` fields. The per-kill
 * `VictoryRecord` list is the part a *career* needs: a claim in 1916 was only
 * credited if somebody else saw it go down or the wreck could be found on your
 * own side of the lines, so the two facts that decide that — how many friendly
 * aeroplanes were still up at the moment, and which side of the front the
 * victim was over — are captured at the instant of the kill and nowhere else.
 * A tick later the witnesses may be dead and the wreck is not where it fell.
 *
 * This module only *records*; `campaign/victory-credit.ts` decides what counts.
 */

import type { CombatEvent } from "../combat/events.ts";
import type { EncounterState } from "../combat/encounter.ts";
import { targetProfileOf } from "../combat/targets.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { isFriendlyTerritory, type FrontLine } from "./mission-world.ts";

export const VICTORY_KINDS = ["aircraft", "balloons", "zeppelins"] as const;

export type VictoryKind = (typeof VICTORY_KINDS)[number];

export interface VictoryRecord {
  readonly kind: VictoryKind;
  /** The callsign or target label of whatever went down. */
  readonly label: string;
  /** Seconds into the sortie. */
  readonly timeS: number;
  /** Whether the wreck fell on the claimant's own side of the lines. */
  readonly overFriendlyTerritory: boolean;
  /** Friendly aeroplanes other than the claimant still flying at that moment. */
  readonly witnesses: number;
}

export interface KillTally {
  readonly aircraftKills: number;
  readonly balloonKills: number;
  readonly zeppelinKills: number;
  readonly squadronKills: number;
  /**
   * The squadron's balloon and Zeppelin claims, the player's own included — the
   * other two rows of the debriefing screen's `SQUADRON` column, which until
   * these existed the screen could only draw as zero.
   *
   * Counted on the same terms as `squadronKills`: any friendly aeroplane's
   * claim, so the `SQUADRON` column is always the `PERSONAL` column plus the
   * rest of the flight, never less than it.
   */
  readonly squadronBalloonKills: number;
  readonly squadronZeppelinKills: number;
  readonly friendlyLosses: number;
  /** Friendly aeroplanes the player himself shot down. The Board of Inquiry's business. */
  readonly friendlyKills: number;
  readonly victories: readonly VictoryRecord[];
}

export const EMPTY_TALLY: KillTally = {
  aircraftKills: 0,
  balloonKills: 0,
  zeppelinKills: 0,
  squadronKills: 0,
  squadronBalloonKills: 0,
  squadronZeppelinKills: 0,
  friendlyLosses: 0,
  friendlyKills: 0,
  victories: [],
};

export interface KillContext {
  readonly playerId: string;
  readonly frontLine: FrontLine;
  readonly encounter: EncounterState;
  /** What the briefing called an aeroplane, so a claim reads as a name. */
  readonly callsignOf: (id: string) => string;
}

function witnessesFor(context: KillContext): number {
  return context.encounter.aircraft.filter(
    (entry) => entry.side === "friendly" && entry.id !== context.playerId && entry.status !== "destroyed",
  ).length;
}

function recordOf(context: KillContext, kind: VictoryKind, label: string, position: Vec3): VictoryRecord {
  return {
    kind,
    label,
    timeS: context.encounter.timeS,
    overFriendlyTerritory: isFriendlyTerritory(context.frontLine, position.z),
    witnesses: witnessesFor(context),
  };
}

function creditTarget(tally: KillTally, context: KillContext, event: CombatEvent): KillTally | undefined {
  const target = context.encounter.targets.find((entry) => entry.id === event.aircraftId);
  if (!target) return undefined;
  const kind = targetProfileOf(target.kind).killCategory;
  // A structure is not a victory: `PS.EXE`'s debriefing has AIRCRAFT, BALLOONS
  // and ZEPPELINS and no fourth column, and a levelled supply dump was never a
  // claim to make. How the raid went is counted off the targets themselves in
  // `mission-run.ts`, not accumulated from events — a shed that is alight is
  // already lost, and the mission is usually decided before it finishes burning.
  if (kind === null) return tally;
  // The squadron's column counts any friendly aeroplane's claim; the personal
  // column and the `victories` list stay the player's own, because only his
  // claims are his to confirm.
  const friendlyIds = new Set(
    context.encounter.aircraft.filter((entry) => entry.side === "friendly").map((entry) => entry.id),
  );
  const squadron = event.sourceId !== null && friendlyIds.has(event.sourceId);
  const withSquadron: KillTally = squadron
    ? kind === "balloons"
      ? { ...tally, squadronBalloonKills: tally.squadronBalloonKills + 1 }
      : { ...tally, squadronZeppelinKills: tally.squadronZeppelinKills + 1 }
    : tally;
  if (event.sourceId !== context.playerId) return withSquadron;
  const record = recordOf(context, kind, target.label, target.position);
  return kind === "balloons"
    ? { ...withSquadron, balloonKills: withSquadron.balloonKills + 1, victories: [...withSquadron.victories, record] }
    : { ...withSquadron, zeppelinKills: withSquadron.zeppelinKills + 1, victories: [...withSquadron.victories, record] };
}

function creditAircraft(tally: KillTally, context: KillContext, event: CombatEvent): KillTally {
  const victim = context.encounter.aircraft.find((entry) => entry.id === event.aircraftId);
  if (!victim) return tally;
  const byPlayer = event.sourceId === context.playerId;
  if (victim.side === "friendly") {
    return {
      ...tally,
      friendlyLosses: tally.friendlyLosses + 1,
      friendlyKills: tally.friendlyKills + (byPlayer && victim.id !== context.playerId ? 1 : 0),
    };
  }
  const friendlyIds = new Set(
    context.encounter.aircraft.filter((entry) => entry.side === "friendly").map((entry) => entry.id),
  );
  const squadron = event.sourceId !== null && friendlyIds.has(event.sourceId);
  if (!byPlayer) return squadron ? { ...tally, squadronKills: tally.squadronKills + 1 } : tally;
  const record = recordOf(context, "aircraft", context.callsignOf(victim.id), victim.sim.position);
  return {
    ...tally,
    aircraftKills: tally.aircraftKills + 1,
    squadronKills: tally.squadronKills + (squadron ? 1 : 0),
    victories: [...tally.victories, record],
  };
}

/**
 * Is this the event that cost the aeroplane the fight?
 *
 * Since AD-014 an aeroplane is not destroyed where it is shot — it goes out of
 * control, falls, and is destroyed by the ground half a minute later. That gap
 * is the whole point of the arc, and it is poison for everything this module
 * records: by the time the wreck lands, the witnesses have flown on, the front
 * line is a kilometre away, and `lastAttackerId` may name whoever last put a
 * burst into a corpse rather than the man who killed it.
 *
 * So a *claim* is booked at the fatal blow, exactly where it always was, and
 * only the explosion waits for the ground. That is one event per aeroplane:
 * `outOfControl` for anything shot down, and `destroyed` only for an aeroplane
 * that never fell at all — one flown straight into the ground, which reaches
 * `destroyed` from `flying` in a single step and has `fallingSeconds` of zero to
 * prove it.
 */
function isFatalBlow(context: KillContext, event: CombatEvent): boolean {
  if (event.kind === "outOfControl") return true;
  const victim = context.encounter.aircraft.find((entry) => entry.id === event.aircraftId);
  return victim !== undefined && victim.fallingSeconds === 0;
}

/**
 * Folds one tick's losses onto the running tally: aeroplanes at the moment they
 * are finished, structures when they are off the board.
 *
 * The two are split because they fail differently. A balloon does not fall out
 * of control — it burns where it is tethered — so `destroyed` *is* its fatal
 * moment and nothing about it moved.
 */
export function foldKills(tally: KillTally, context: KillContext): KillTally {
  let next = tally;
  for (const event of context.encounter.events) {
    if (event.kind === "destroyed") {
      const asTarget = creditTarget(next, context, event);
      if (asTarget) {
        next = asTarget;
        continue;
      }
    } else if (event.kind !== "outOfControl") {
      continue;
    }
    if (isFatalBlow(context, event)) next = creditAircraft(next, context, event);
  }
  return next;
}
